// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import * as moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { IObservationParam } from "../models/configuration";
import { IGraphExtraInfos } from "../models/externalRessource";
import { IRuleDefinition, IRuleResult } from "../models/rule";
import {
  AccessLevel,
  ACTION_STATUS_ENTITY,
  Codes,
  Identifier,
  IEntity,
  Quantity,
  Reference,
  STATUS_ENTITY,
} from "../models/sharedInterfaces";
import { ITranslation } from "../models/translation";
import { ConfigurationService } from "../services/globalDataProvider/configuration.service";
import { MedicalBluetoothService } from "../services/medical-bluetooth/medical-bluetooth.service";
import { IEnableWhen } from "./enableWhen-helper";
import { FHIRHelper } from "./FHIR-helper";
import { FileLogger } from "./fileLogger";
import { Tools } from "./tools-helper";

export interface OComponent {
  code: Codes;
  valueQuantity: Quantity;
  valuePictures?: string[]; // Array containing pictures in base64
}

/**
 * Help to interpret range observations
 */
export interface IObservationMeaning {
  value: number;
  description: ITranslation; // description with translations: FR,EN,NL
}

/**
 * Observation Definition component use to display Observation
 */
export interface IObservationDefinitionComponent {
  name?: string[]; // Human readable name with translations: [FR,EN,NL]
  nameTranslation?: ITranslation;
  shortName?: string[]; // if name is too long use that array
  shortnameTranslation?: ITranslation;
  loinc?: string;
  type: "range" | "number" | "bool" | "automatic";
  unit: string;
  min?: number; // Minimum value
  max?: number; // Maximum value
  meaning?: IObservationMeaning[]; // Provides guide for "range" interpretation
  step?: number; // if exist in range composant create a slider from min to max with that step
  required?: boolean;
  enableWhen?: IEnableWhen[];
  recommendation?: {
    targets?: Reference[]; // contains the list of loinc usefull for the results
    message?: IRuleResult[]; // the message for the recommendation
  };
  askPicture?: boolean;
  display?: ComponentDisplay[];
  showMethod?: number; // 0 = number, 1 = smiley sad to happy, -1 = smiley happy to sad
}

export enum ComponentDisplay {
  DISPLAY_ALL = 0,
  // NO_PATIENT_MANUAL_ANSWER = 1,
  // NO_PRACTITIONER_MANUAL_ANSWER = 2,
  NO_PATIENT_TEXT_DISPLAY = 3,
  // NO_PRACTITIONER_TEXT_DISPLAY = 4,
  NO_PATIENT_GRAPH_DISPLAY = 5,
  // NO_PRACTITIONER_GRAPH_DISPLAY = 5,
}

/**
 * Observation Definition use to display Observation
 */
export interface IObservationDefinition {
  name: string[]; // Human readable name with translations: [FR,EN,NL]
  nameTranslation?: ITranslation;
  description: ITranslation; // description with translations: FR,EN,NL
  shortnameTranslation?: ITranslation;
  loinc: string;
  chartType: string;
  graphExtraInfos?: IGraphExtraInfos;
  onePerDay: boolean;
  // Observation may be composed of sub-observation,
  // like blood pressure: systolic and/or diastolic
  components: IObservationDefinitionComponent[];
  askTimingWhen?: boolean; // indique s'il faut demander le moment de prise au patient
  lsTimingWhen?: [
    {
      // liste des moments à proposer
      when: {
        code: string;
        display: string;
        system: string;
      };
      name: ITranslation;
    }
  ];
}

/**
 * kind of mesure to take for observation
 */
// tslint:disable-next-line: class-name
export interface OBS_MESURE {
  loinc: string; // code from LOINC.ORG
  label: string;
}

export interface IEffectiveTiming {
  repeat: {
    count: number;
    when: {
      code: string;
      display?: string;
      system?: string;
    };
  };
}

export interface IObservationWithoutComponents extends IEntity {
  resourceType: string;
  identifier: Identifier[];
  status: string;
  category: Codes;
  code: Codes;
  subject: Reference;
  encounter?: Reference;
  issued: string;
  performer?: Reference[];
  comments?: string;
  idNotification?: number;
  effectiveTiming?: IEffectiveTiming;
  device?: {
    reference: string;
    type: MedicalBluetoothService;
    componentAnswer: {
      loinc: string; // contains the component's loinc list of the observation that are filled by the bluetooth device
    }[]; // not FHIR
  };
  linkedRuleAlerts?: ILinkedRuleAlert[];
  questionnaireResponseRef?: string; // identifier.value of the possible linked questionnaire response
  patientAccess?: AccessLevel; // whether the patient can see and/or edit this observation
}

export interface ILinkedRuleAlert {
  identifier: Identifier[];
  rule: IRuleDefinition;
  originalComponents?: OComponent[]; // this field is used when the observation is modified by the patient: this modification may generate a new rule or no rule at all; in this case, the values before modification are saved in this field for alerts already existing in the observation, to keep track of the values which generated these alerts
  originalIssued?: string; // this field is used when the observation is modified by the patient: this modification may generate a new rule or no rule at all; in this case, the values before modification are saved in this field for alerts already existing in the observation, to keep track of the values which generated these alerts
  originalEffectiveTiming?: IEffectiveTiming; // this field is used when the observation is modified by the patient: this modification may generate a new rule or no rule at all; in this case, the values before modification are saved in this field for alerts already existing in the observation, to keep track of the values which generated these alerts
}

export interface IObsByCategory {
  observations: IObservation[];
  param: IObservationParam;
  definition: IObservationDefinition;
}

/**
 * Interface for Observation
 */
export interface IObservation extends IObservationWithoutComponents {
  component: OComponent[];
}

/**
 * Helper for FHIR Observation
 */
export class Observation {
  /**
   * get Value of this observation
   * @param observation
   * @param code
   */
  public static getValue(observation: IObservation, code: string): number {
    try {
      for (const compo of observation.component) {
        if (compo.code.coding[0].code === code) return compo.valueQuantity.value;
      }
    } catch (err) {
      return 0;
    }
  }

  /**
   *
   * @param obs
   */
  public static identifier2Reference(obs: IObservation): Reference {
    try {
      const identifier = FHIRHelper.getCaremateIdentifier(obs);
      return {
        reference: identifier.value,
        display: identifier.value,
      };
    } catch (err) {
      FileLogger.error("Observation", "identifier2Reference failed", err);
    }
  }

  /**
   *  create an empty observation base on model
   */
  public static createObservation(definition: IObservationDefinition, lang: string): IObservation {
    return {
      _id: Tools.genValidId(),
      actionStatus: ACTION_STATUS_ENTITY.CREATED,
      entityStatus: [STATUS_ENTITY.ACTIVE],
      issued: Tools.nowISO8601(),
      modified: null,

      // "bodySite": null,
      category: {
        // TODO the category is probably not always the same.
        coding: [
          {
            code: "vital-signs",
            display: "Vital Signs",
            system: "http://hl7.org/fhir/observation-category",
          },
        ],
      },
      code: {
        coding: [
          {
            code: definition.loinc,
            display: definition.nameTranslation
              ? definition.nameTranslation[lang]
              : definition.name
              ? definition.name[0]
              : definition.loinc,
            system: "http://loinc.org",
          },
        ],
      },
      comments: null,
      component: definition.components.map((component) => ({
        code: {
          coding: [
            {
              code: component.loinc || definition.loinc,
              display: component.nameTranslation
                ? component.nameTranslation[lang]
                : component.name
                ? component.name[0]
                : component.loinc || definition.nameTranslation
                ? definition.nameTranslation[lang]
                : definition.name
                ? definition.name[0]
                : definition.loinc,
              system: "http://loinc.org",
            },
          ],
        },
        valueQuantity: {
          code: null,
          system: null,
          unit: component.unit || null,
          value: component.type === "range" && (component.showMethod === 1 || component.showMethod === -1) ? component.min : null,
        },
      })),
      encounter: null,
      identifier: [
        {
          use: FHIRHelper.USUAL,
          system: FHIRHelper.SYSTEM_CAREMATE,
          value: "observation/" + uuidv4(),
          label: FHIRHelper.SYSTEM_CAREMATE,
        },
      ],
      performer: null,
      resourceType: "Observation",
      status: "final",
      subject: null,
      patientAccess: AccessLevel.WRITE,
    };
  }

  /**
   * return OBSERVATION_TYPE of this observation
   */
  public static getObservationType(observation: IObservation): string {
    try {
      return observation.code.coding[0].code;
    } catch (e) {
      return null;
    }
  }

  /**
   * Return OComponent from observation
   * @param observation
   * @param code
   */
  public static getComponent(observation: IObservation, code: string): OComponent {
    for (const c of observation.component) {
      if (c.code.coding[0].code === code) return c;
    }
    return null;
  }

  public static defaultEffectiveTime(definition: IObservationDefinition, date: string, configService: ConfigurationService): string {
    if (!definition.askTimingWhen || !definition.lsTimingWhen?.length) {
      return null;
    }

    const now = moment(date);
    const isWeekend = Tools.isWeekend(now);
    const minSinceMidnight = Tools.toMinSinceMidnigth(now);

    const timingCodeNotif = definition.lsTimingWhen
      .filter((tw) => IEntity.LS_TIMING.includes(tw.when?.code))
      .map((tw) => {
        return {
          code: tw.when.code,
          // |now - timing define in notification|
          value: Math.abs(configService.getUserTiming(IEntity.fromTimingCode(tw.when.code), isWeekend) - minSinceMidnight),
        };
      })
      .sort((code1, code2) => code1.value - code2.value);

    // if there is no code related to notifications, the default value is the first element of the table
    if (!timingCodeNotif.length) {
      return definition.lsTimingWhen[0]?.when?.code;
    }

    // the code associated to the minimum of |now - timing define in notification|
    return timingCodeNotif[0].code;
  }
}
